import Table, { Column } from "../../../common/table";
import { useTranslation } from "react-i18next"
import { Link} from "react-router-dom";
import { Condition } from "../../../../types/Condition";
import { formatToShortCst } from "../../../../utils/formatToShortCst";
import { classNames } from "../../../../utils/classNamesUtils";
import { Alert } from "../../../../types/Alert";
import { FieldgistixSwitch } from "../../../common/FieldgistixSwitch";
import { useState } from "react";
import AlertHoverPopup from "./AlertHoverPopup";

function findCondtionName(conditions: Condition[], conditionId: string) {
  const condition = conditions?.find((condition) => condition._id === conditionId);
  
  return condition?.name;
}

interface Props {
  alertData: any
}

export default function DeviceAlertTable(props: Props) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(true);

  const alertTypeStyles = {
    MONITOR: "bg-orange-100 text-yellow-800 text-center",
    SYSTEM_NO_DATA: "bg-red-100 text-blue-800 text-center",
    TELEMATIC_FAULT: "bg-orange-100 text-yellow-800 text-center",
  };
  
  const alertTypeLabel = {
    MONITOR: t('sites.monitor'),
    SYSTEM_NO_DATA: t('sites.deviceDisconected'),
    TELEMATIC_FAULT: t('sites.telematicFault'),
  }

  const columns: Column<Alert>[] = [
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-center text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.createdAt')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td
          key={colI}
          className="hide-on-mobile whitespace-nowrap px-6 py-2 text-center text-xs text-gray-500"
        >
          {formatToShortCst(row.createdAt || '')}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-center text-sm font-semibold text-gray-900"
          scope="col"
        >
        {t('sites.type')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td
          key={colI}
          className="px-6 py-2 text-center" // Center the <td> content
        >
          <div
            className={classNames(
              //@ts-ignore
              row.triggerType && alertTypeStyles[row.triggerType],
              "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
            )}
          >
             {/* @ts-ignore */}
            {row.triggerType && alertTypeLabel[row.triggerType]}
          </div>
        </td>
      ), 
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-center text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.monitor')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-center text-xs text-gray-500">
          {row.monitor ? (
            <Link
              to={`/monitors/${row?.monitor?._id}`}
              className="text-blue-600 hover:underline"
            >
              {row.monitor?.name}
            </Link>
          ) : (
            '-'
          )}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-center text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.condition')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-center text-xs text-gray-500">
          {row.monitor && row.monitor?.conditions ? (
            <Link
              to={`/monitors/${row.monitor?._id}/conditions/${row.conditionId}`}
              className="text-blue-600 hover:underline"
            >
              {findCondtionName(row.monitor?.conditions, row.conditionId)}
            </Link>
          ) : (
            '-'
          )}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-center text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.priority')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-center text-xs text-gray-500">
          P{row.priority}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-center text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.muted')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td
          key={colI}
          className="hide-on-mobile whitespace-nowrap px-6 py-2 text-center text-xs text-gray-500"
        >
          <input
            type="checkbox"
            checked={row.muted}
            className="mt-1 w-5 h-5 rounded-md border border-gray-300"
            disabled
          />
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-center text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.cleared')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td
          key={colI}
          className="hide-on-mobile whitespace-nowrap px-6 py-2 text-center text-xs text-gray-500"
        >
          <input
            type="checkbox"
            checked={row.cleared}
            className="mt-1 w-5 h-5 rounded-md border border-gray-300"
            disabled
          />
        </td>
      ),
    },
  ];

  const renderAlertPopup = (alert: Alert) => {
    // Only show popup for TELEMATIC_FAULT alerts
    // @ts-ignore
    if (alert.triggerType === 'TELEMATIC_FAULT') {
      return <AlertHoverPopup alert={alert} />;
    }
    return null;
  };

  return (
    <>
      <div className="flex justify-end mt-3">
        <FieldgistixSwitch label="Active Alerts" checked={checked} onChange={setChecked}></FieldgistixSwitch>
      </div>
      <Table
        columns={columns}
        rows={ !checked ? props.alertData : props.alertData.filter((alert: Alert) => !alert.cleared)}
        title={t('sites.alerts')}
        currentPage={1}
        pageSize={1}
        totalRows={1}
        showPagination={false}
        removePadding={true}
        renderHoverPopup={renderAlertPopup}
      ></Table>
    </>
  );
}